import React, { cloneElement, isValidElement, useId } from "react";
import { AlertCircle } from "lucide-react";

/**
 * ─── FormField ─────────────────────────────────────────────────────────────
 * The ONLY way to render a labelled form control in the Owner Console
 * (PDR v2.1 §8, item #5). Wraps <Input/>, <Textarea/>, <Select/>, etc.
 *
 * Props
 *   label       string          — required
 *   required    boolean         — appends a subtle red asterisk
 *   description string          — helper text below the label
 *   error       string          — validation error text; enables error state
 *   htmlFor     string          — override auto-generated id
 *   testId
 *   children    single form-control element (Input, Textarea, Select…)
 *                 If children accept `id` + `error` props, they are auto-wired.
 */
export default function FormField({
  label, required = false, description, error,
  htmlFor, testId, children,
}) {
  const autoId = useId();
  const id = htmlFor || autoId;
  const labelId = `${id}-label`;

  let control = children;
  if (isValidElement(children)) {
    const extra = {};
    // Native inputs use `id` + <label htmlFor>. Custom Select trigger is a
    // <button> and cannot be labelled via htmlFor. We pass BOTH the DOM-safe
    // kebab-case `aria-labelledby` (understood by native <input> / <textarea>
    // and forwarded by our Select) AND avoid the camelCase form that React
    // warns about when applied to DOM elements.
    if (!children.props.id)      extra.id = id;
    if (children.props.error === undefined && error !== undefined) extra.error = !!error;
    if (label && children.props["aria-labelledby"] === undefined && children.props.ariaLabelledBy === undefined) {
      extra["aria-labelledby"] = labelId;
    }
    if (Object.keys(extra).length) control = cloneElement(children, extra);
  }

  return (
    <div className="ds-field" data-testid={testId}>
      {label && (
        <label id={labelId} className="ds-field__label" htmlFor={id}>
          <span>{label}</span>
          {required && <span className="ds-field__required" aria-hidden>*</span>}
        </label>
      )}
      {control}
      {description && !error && (
        <div className="ds-field__desc" data-testid={testId ? `${testId}-desc` : undefined}>{description}</div>
      )}
      {error && (
        <div className="ds-field__error" role="alert" data-testid={testId ? `${testId}-error` : undefined}>
          <AlertCircle size={12} strokeWidth={2.4} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
